import React, { forwardRef, useMemo, useRef, useState, useEffect } from "react";
import Plot from "react-plotly.js";
import "./plot.scss";
// 🔧 Responsive title formatter (80% width)
const formatResponsiveTitle = (text, containerWidth) => {
  if (!text) return "";

  const usableWidth = containerWidth * 0.8;

  // approx character width for font size ~14px
  const charWidth = 7;
  const maxCharsPerLine = Math.max(10, Math.floor(usableWidth / charWidth));

  const words = text.split(" ");
  let lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if ((currentLine + word).length > maxCharsPerLine) {
      lines.push(currentLine.trim());
      currentLine = word + " ";
    } else {
      currentLine += word + " ";
    }
  });

  if (currentLine) lines.push(currentLine.trim());

  return lines.join("<br>");
};

const PlotComponent = forwardRef(
  ({ plotKey, data, layout, config, plotTitles }, plotRef) => {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(600);

    // 📏 Track container width (responsive)
    useEffect(() => {
      const handleResize = () => {
        if (containerRef.current) {
          setWidth(containerRef.current.offsetWidth - 250);
        }
      };

      handleResize(); // initial

      const resizeObserver = new ResizeObserver(handleResize);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, []);

    const memoizedLayout = useMemo(() => {
      const titleText = formatResponsiveTitle(
        plotTitles?.main_title || "",
        width,
      );

      const lineCount = titleText.split("<br>").length;

      const baseTopMargin = 24;
      const extraPerLine = 16;

      return {
        ...layout,
        autosize: true,

        margin: {
          t: baseTopMargin + (lineCount - 1) * extraPerLine,
          b: 60,
          l: 50,
          r: 180,
        },

        title: {
          text: titleText,
          // x: 0.09, // start after ~10% (so visually ~80% usable)
          xanchor: "center",
          font: {
            size: 14,
            weight: 600,
            color: "#1B72B7",
          },
        },

        xaxis: {
          ...layout?.xaxis,
          title: {
            text: plotTitles?.x_axis_title || "",
            font: { size: 14, weight: 600 },
            standoff: 20,
          },
        },

        yaxis: {
          ...layout?.yaxis,
          title: {
            text: plotTitles?.y_axis_title || "",
            font: { size: 14, weight: 600 },
          },
        },

        legend: {
          // orientation: "h", // horizontal legend (usually top)
          y: 0.86, // move slightly down (default is ~1)
          yanchor: "top",
          // x: 0.5,
          // xanchor: "center",
        },

        annotations: (layout?.annotations || []).map((ann) => ({
          ...ann,
          x: 1.05,
          y: 0.15,
          xref: "paper",
          yref: "paper",
          xanchor: "left",
          yanchor: "top",
          text: ann.text?.replace(/\n/g, "<br>"),
        })),
      };
    }, [layout, plotTitles, width]);

    return (
      <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
        <Plot
          ref={plotRef}
          key={plotKey}
          data={data}
          layout={memoizedLayout}
          config={{
            ...config,
            responsive: true,
            scrollZoom: true,
            displaylogo: false,
          }}
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    );
  },
);

// Memoize the component to prevent unnecessary re-renders when props are unchanged
export default React.memo(PlotComponent, (prevProps, nextProps) => {
  return (
    prevProps.plotKey === nextProps.plotKey &&
    prevProps.data === nextProps.data &&
    prevProps.layout === nextProps.layout &&
    prevProps.config === nextProps.config &&
    prevProps.plotTitles === nextProps.plotTitles
  );
});